import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslations } from "../../../i18n";
import {
  useStudioPackages,
  type PackagePass,
  type PackageSubscription,
} from "../../hooks/use-studio-packages";
import { useAuthStore } from "../../store/auth-store";
import { useViewStore } from "../../store/view-store";
import { useStudioStore } from "../../store/studio-store";
import { api, ApiError } from "../../lib/api";
import { withLocale } from "../../lib/widget-locale";
import EmbeddedStripeCheckout from "./booking/_components/EmbeddedStripeCheckout";
import PassPromoStep, {
  type AppliedCode,
  type PassPaymentMethod,
} from "./packages/pass-promo-step";

export default function PackagesPage() {
  const t = useTranslations("packages");
  const { passes, subscriptions, isLoading } = useStudioPackages();
  const { isAuthenticated } = useAuthStore();
  const { navigate } = useViewStore();
  const studio = useStudioStore((s) => s.studio);

  const [activeTab, setActiveTab] = useState<"passes" | "memberships">(
    "passes",
  );
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<
    string | null
  >(null);
  const [checkoutPassTitle, setCheckoutPassTitle] = useState<string | null>(
    null,
  );
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  // Etape intermediaire ou saisir un code promo : sans elle, le bouton
  // « Acheter » envoyait directement sur Stripe et le code n'avait nulle part
  // ou etre entre.
  const [pendingPass, setPendingPass] = useState<PackagePass | null>(null);

  const currency = (studio?.currency ?? "CHF").toUpperCase();

  const formatPrice = (price: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(price);

  const handleBuyPass = (pass: PackagePass) => {
    if (!isAuthenticated) {
      navigate("login", { intended: "packages" });
      return;
    }

    setPendingPass(pass);
  };

  const startCheckout = async (
    applied?: AppliedCode,
    paymentMethod?: PassPaymentMethod,
    autoBookCourseId?: string,
  ) => {
    if (!pendingPass) return;

    try {
      setIsCheckingOut(true);
      // La langue sert a nommer le produit sur la page Stripe : sans elle,
      // l'acheteuse bascule dans la langue de saisie du studio au moment de
      // payer, c'est-a-dire au pire moment.
      const data = await api(withLocale("/api/widget/packages/checkout"), {
        method: "POST",
        body: JSON.stringify({
          passId: pendingPass.id,
          paymentMethod: paymentMethod ?? "CARD",
          ...(autoBookCourseId ? { autoBookCourseId } : {}),
          ...(applied?.type === "DISCOUNT_CODE"
            ? { promoCode: applied.code }
            : {}),
          ...(applied?.type === "GIFT_CARD"
            ? { giftCardCode: applied.code }
            : {}),
        }),
      });

      // La carte cadeau couvre tout : le pass est deja attribue, il n'y a
      // aucune session Stripe a ouvrir.
      if (data.requiresPayment === false) {
        setPendingPass(null);
        toast.success(t("purchaseSuccessful"));
        navigate("profile", { profileTab: "passes" });
        return;
      }

      setCheckoutClientSecret(data.clientSecret);
      setCheckoutPassTitle(pendingPass.title);
      setPendingPass(null);
    } catch (err) {
      // Le serveur revalide le code : s'il le refuse a cet instant, la cliente
      // doit savoir que c'est le code qui bloque, pas le paiement.
      const reason =
        err instanceof ApiError
          ? (err.body as { error?: string })?.error
          : undefined;

      toast.error(
        reason === "AUTO_BOOK_COURSE_REQUIRED"
          ? t("autoBookCourseRequired")
          : reason === "GIFT_CARD_REJECTED"
          ? t("giftCardRejectedAtCheckout")
          : reason === "PROMO_CODE_REJECTED"
            ? t("promoRejectedAtCheckout")
            : // Le studio n'a pas connecte Stripe, ou la methode choisie n'est
              // plus active entre-temps : reessayer n'y changera rien, il faut
              // le dire plutot que d'inviter a recommencer.
              reason === "STRIPE_NOT_CONNECTED" ||
                reason === "PAYMENT_METHOD_UNAVAILABLE"
              ? t("cardPaymentsUnavailable")
              : t("checkoutStartFailed"),
      );
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleCheckoutComplete = () => {
    setCheckoutClientSecret(null);
    setCheckoutPassTitle(null);
    toast.success(t("purchaseSuccessful"));
    navigate("profile", { profileTab: "passes" });
  };

  const handleCheckoutCancel = () => {
    setCheckoutClientSecret(null);
    setCheckoutPassTitle(null);
  };

  if (pendingPass && !checkoutClientSecret) {
    return (
      <PassPromoStep
        pass={pendingPass}
        studioId={studio?.id ?? ""}
        formatPrice={formatPrice}
        isCheckingOut={isCheckingOut}
        onBack={() => setPendingPass(null)}
        onConfirm={startCheckout}
      />
    );
  }

  if (checkoutClientSecret) {
    return (
      <div className="w-full p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={handleCheckoutCancel}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← {t("cancelPayment")}
            </button>
            {checkoutPassTitle && (
              <span className="text-sm font-medium">{checkoutPassTitle}</span>
            )}
          </div>
          <EmbeddedStripeCheckout
            clientSecret={checkoutClientSecret}
            onComplete={handleCheckoutComplete}
            onCancel={handleCheckoutCancel}
          />
        </div>
      </div>
    );
  }

  return (
    <section className="w-full bg-muted/30 p-4 min-h-[400px]">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6 text-center">
          <h2 className="text-xl font-bold text-foreground">{t("title")}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t("subtitle")}</p>
        </div>

        {/* Tabs — only shown when both types exist */}
        {!isLoading && passes.length > 0 && subscriptions.length > 0 && (
          <div className="flex gap-1 mb-6 bg-muted/50 p-1 rounded-xl w-fit mx-auto">
            <TabButton
              active={activeTab === "passes"}
              onClick={() => setActiveTab("passes")}
              label={t("passes")}
            />
            <TabButton
              active={activeTab === "memberships"}
              onClick={() => setActiveTab("memberships")}
              label={t("memberships")}
            />
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : passes.length === 0 || activeTab === "memberships" ? (
          <MembershipsGrid
            subscriptions={subscriptions}
            formatPrice={formatPrice}
            t={t}
            isAuthenticated={isAuthenticated}
            onLoginPrompt={() => navigate("login", { intended: "packages" })}
          />
        ) : (
          <PassesGrid
            passes={passes}
            formatPrice={formatPrice}
            t={t}
            onBuy={handleBuyPass}
            isAuthenticated={isAuthenticated}
            isCheckingOut={isCheckingOut}
          />
        )}
      </div>
    </section>
  );
}

/* ── Sub-components ──────────────────────────────────────────────── */

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
        active
          ? "bg-white text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function PassesGrid({
  passes,
  formatPrice,
  t,
  onBuy,
  isAuthenticated,
  isCheckingOut,
}: {
  passes: PackagePass[];
  formatPrice: (n: number) => string;
  t: (key: string, opts?: any) => string;
  onBuy: (pass: PackagePass) => void;
  isAuthenticated: boolean;
  isCheckingOut: boolean;
}) {
  if (passes.length === 0) {
    return <EmptyState message={t("noPasses")} />;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {passes.map((pass) => (
        <PassCard
          key={pass.id}
          pass={pass}
          formatPrice={formatPrice}
          t={t}
          onBuy={onBuy}
          isAuthenticated={isAuthenticated}
          isCheckingOut={isCheckingOut}
        />
      ))}
    </div>
  );
}

function PassCard({
  pass,
  formatPrice,
  t,
  onBuy,
  isAuthenticated,
  isCheckingOut,
}: {
  pass: PackagePass;
  formatPrice: (n: number) => string;
  t: (key: string, opts?: any) => string;
  onBuy: (pass: PackagePass) => void;
  isAuthenticated: boolean;
  isCheckingOut: boolean;
}) {
  const creditsLabel =
    pass.credits === null
      ? t("unlimited")
      : t("classes", { count: pass.credits });

  return (
    <div
      className={`relative flex flex-col rounded-2xl border bg-white p-5 shadow-sm transition-shadow hover:shadow-md ${
        pass.isPopular
          ? "border-primary ring-1 ring-primary/30"
          : "border-border"
      }`}
    >
      {pass.isPopular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-white shadow">
          {t("popular")}
        </span>
      )}

      <div className="mb-4">
        <h3 className="font-semibold text-base text-foreground">
          {pass.title}
        </h3>
        {pass.description && (
          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
            {pass.description}
          </p>
        )}
      </div>

      <div className="mb-4 flex-1">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-foreground">
            {formatPrice(pass.price)}
          </span>
        </div>

        <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
          <li className="flex items-center gap-2">
            <CheckIcon />
            {creditsLabel}
          </li>
          {pass.durationDays != null && (
            <li className="flex items-center gap-2">
              <CheckIcon />
              {t("validFor", { count: pass.durationDays })}
            </li>
          )}
          {(pass.autoBookCourses?.length ?? 0) > 0 && (
            <li className="flex items-center gap-2">
              <CheckIcon />
              {t("autoBookBadge")}
            </li>
          )}
        </ul>
      </div>

      <button
        disabled={isCheckingOut}
        onClick={() => onBuy(pass)}
        className={`cursor-pointer mt-auto w-full rounded-xl py-2.5 text-sm font-semibold transition-colors ${
          pass.isPopular
            ? "bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
            : "border border-primary text-primary hover:bg-primary/5 disabled:opacity-60"
        }`}
      >
        {t("chooseThisPass")}
      </button>
    </div>
  );
}

function MembershipsGrid({
  subscriptions,
  formatPrice,
  t,
  isAuthenticated,
  onLoginPrompt,
}: {
  subscriptions: PackageSubscription[];
  formatPrice: (n: number) => string;
  t: (key: string, opts?: any) => string;
  isAuthenticated: boolean;
  onLoginPrompt: () => void;
}) {
  if (subscriptions.length === 0) {
    return <EmptyState message={t("noMemberships")} />;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {subscriptions.map((sub) => (
        <MembershipCard
          key={sub.id}
          subscription={sub}
          formatPrice={formatPrice}
          t={t}
          isAuthenticated={isAuthenticated}
          onLoginPrompt={onLoginPrompt}
        />
      ))}
    </div>
  );
}

function MembershipCard({
  subscription: sub,
  formatPrice,
  t,
  isAuthenticated,
  onLoginPrompt,
}: {
  subscription: PackageSubscription;
  formatPrice: (n: number) => string;
  t: (key: string, opts?: any) => string;
  isAuthenticated: boolean;
  onLoginPrompt: () => void;
}) {
  const intervalLabel = sub.interval === "year" ? t("perYear") : t("perMonth");

  const creditsLabel =
    sub.creditsPerPeriod === null
      ? t("unlimitedCredits")
      : t("creditsPerPeriod", { count: sub.creditsPerPeriod });

  return (
    <div
      className={`relative flex flex-col rounded-2xl border bg-white p-5 shadow-sm transition-shadow hover:shadow-md ${
        sub.isPopular
          ? "border-primary ring-1 ring-primary/30"
          : "border-border"
      }`}
    >
      {sub.isPopular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-white shadow">
          {t("popular")}
        </span>
      )}

      <div className="mb-4">
        <h3 className="font-semibold text-base text-foreground">{sub.title}</h3>
        {sub.description && (
          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
            {sub.description}
          </p>
        )}
      </div>

      <div className="mb-4 flex-1">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-foreground">
            {formatPrice(sub.price)}
          </span>
          <span className="text-sm text-muted-foreground">{intervalLabel}</span>
        </div>

        <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
          <li className="flex items-center gap-2">
            <CheckIcon />
            {creditsLabel}
          </li>
        </ul>
      </div>

      <button
        onClick={onLoginPrompt}
        className={`cursor-pointer mt-auto w-full rounded-xl py-2.5 text-sm font-semibold transition-colors ${
          sub.isPopular
            ? "bg-primary text-white hover:bg-primary/90"
            : "border border-primary text-primary hover:bg-primary/5"
        }`}
      >
        {t("chooseThisPass")}
      </button>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-3 text-3xl">📦</div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      className="h-4 w-4 shrink-0 text-primary"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="2 8 6 12 14 4" />
    </svg>
  );
}
